/**
 * La navigation, définie UNE FOIS et partagée entre l'ordinateur et le téléphone.
 *
 * Les libellés sont ceux d'un loueur de voitures, pas ceux d'un développeur :
 * on dit « Mes voitures », pas « Flotte » ; « À valider », pas « Demandes » ;
 * « Trésorerie & prévision », pas « Prévision & capacité ».
 *
 * Les regroupements suivent la façon dont l'agence pense son métier — le comptoir,
 * les voitures, les clients, l'argent, les chiffres — et non l'organisation du code.
 */
export interface Entree {
  to: string;
  label: string;
  icone: string;
  /** Capacité requise ; absente = visible par tout le monde. */
  droit?: string;
  /** Masqué pour l'agent de comptoir même s'il en a le droit. */
  gerantsSeulement?: boolean;
  /**
   * Sous-entrées, dépliées quand on est sur le parent ou l'une d'elles.
   * L'entretien en a une par type : le patron ne cherche pas « une
   * intervention », il cherche « où en est la vidange sur mes voitures ».
   */
  sous?: Entree[];
}
export interface Groupe { titre: string; entrees: Entree[] }

export const MENU: Groupe[] = [
  {
    titre: 'Au comptoir',
    entrees: [
      { to: '/', label: "Aujourd'hui", icone: '☀' },
      { to: '/disponibilites', label: 'Voitures libres', icone: '🔍' },
      { to: '/reservations/nouvelle', label: 'Nouvelle location', icone: '＋' },
      { to: '/locations', label: 'Mes locations', icone: '📋' },
      { to: '/contrats', label: 'Contrats', icone: '📄', droit: 'voir.contrats' },
      { to: '/planning', label: 'Planning', icone: '▦', gerantsSeulement: true },
      { to: '/corrections', label: 'À valider', icone: '⚑' },
    ],
  },
  {
    titre: 'Mes voitures',
    entrees: [
      { to: '/flotte', label: 'Mes voitures', icone: '🚗' },
      {
        to: '/entretien', label: 'Entretien', icone: '🔧', gerantsSeulement: true,
        sous: [
          { to: '/entretien/vidange', label: 'Vidange', icone: '·', gerantsSeulement: true },
          { to: '/entretien/plaquettes', label: 'Plaquettes', icone: '·', gerantsSeulement: true },
          { to: '/entretien/pneus', label: 'Pneus', icone: '·', gerantsSeulement: true },
          { to: '/entretien/revision', label: 'Révision', icone: '·', gerantsSeulement: true },
          { to: '/entretien/courroie', label: 'Courroie', icone: '·', gerantsSeulement: true },
          { to: '/entretien/batterie', label: 'Batterie', icone: '·', gerantsSeulement: true },
        ],
      },
      { to: '/controle-technique', label: 'Contrôle technique', icone: '📋', gerantsSeulement: true },
      { to: '/assurances', label: 'Assurances', icone: '🛡', gerantsSeulement: true },
      { to: '/amendes', label: 'Amendes', icone: '⚠', gerantsSeulement: true },
      { to: '/sinistres', label: 'Accidents', icone: '💥', gerantsSeulement: true },
    ],
  },
  {
    titre: 'Mes clients',
    entrees: [
      { to: '/clients', label: 'Clients', icone: '👤' },
    ],
  },
  {
    titre: "L'argent",
    entrees: [
      { to: '/caisse', label: 'Caisse', icone: '▤' },
      { to: '/banque', label: 'Banque', icone: '🏦', droit: 'modifier.finance' },
      { to: '/cautions', label: 'Cautions', icone: '🛡', gerantsSeulement: true },
      { to: '/factures', label: 'Factures', icone: '🧾', gerantsSeulement: true },
    ],
  },
  {
    titre: 'Mes chiffres',
    entrees: [
      { to: '/capacite', label: 'Trésorerie & prévision', icone: '◪', droit: 'voir.analytique' },
      { to: '/analytique', label: 'Analyses', icone: '📈', droit: 'voir.analytique' },
      { to: '/journal', label: "Journal d'activité", icone: '☰', droit: 'voir.journal' },
    ],
  },
  {
    titre: 'Réglages',
    entrees: [
      { to: '/parametres', label: 'Paramètres', icone: '⚙', droit: 'gerer.parametres' },
      { to: '/utilisateurs', label: 'Utilisateurs et droits', icone: '🔑', droit: 'gerer.utilisateurs' },
    ],
  },
];

/** Ce que la personne connectée a réellement le droit de voir. */
export function menuPour(peut: (c: string) => boolean, estAgent: boolean): Groupe[] {
  const autorisee = (e: Entree) =>
    !(e.gerantsSeulement && estAgent) && (!e.droit || peut(e.droit));
  return MENU
    .map((g) => ({
      ...g,
      entrees: g.entrees.filter(autorisee).map((e) => (
        e.sous ? { ...e, sous: e.sous.filter(autorisee) } : e
      )),
    }))
    .filter((g) => g.entrees.length > 0);
}

/**
 * Les onglets du bas sur telephone : les gestes du quotidien, plus « Plus ».
 * Tout le reste vit derrière « Plus » — mais TOUT reste atteignable.
 */
export const ongletsPour = (estAgent: boolean): Entree[] =>
  estAgent
    ? [
        { to: '/', label: 'Mes tâches', icone: '☀' },
        { to: '/disponibilites', label: 'Libres', icone: '🔍' },
        { to: '/reservations/nouvelle', label: 'Nouvelle', icone: '＋' },
        { to: '/caisse', label: 'Caisse', icone: '▤' },
      { to: '/banque', label: 'Banque', icone: '🏦', droit: 'modifier.finance' },
        { to: '/plus', label: 'Plus', icone: '⋯' },
      ]
    : [
        { to: '/', label: "Aujourd'hui", icone: '☀' },
        { to: '/disponibilites', label: 'Libres', icone: '🔍' },
        { to: '/reservations/nouvelle', label: 'Nouvelle', icone: '＋' },
        { to: '/caisse', label: 'Caisse', icone: '▤' },
      { to: '/banque', label: 'Banque', icone: '🏦', droit: 'modifier.finance' },
        { to: '/plus', label: 'Plus', icone: '⋯' },
      ];

/**
 * Ce qu'il faut pour ouvrir un ecran donne.
 *
 * Derive du MENU lui-meme : une entree cachee dans le menu doit etre refusee
 * quand on tape son adresse directement. Sans ce garde, l'ecran s'ouvrait, son
 * chargement partait en 403, et l'utilisateur restait devant un squelette gris
 * sans un mot — il croit le logiciel casse.
 */
export function exigencePour(chemin: string): { droit?: string; gerantsSeulement?: boolean } | null {
  const toutes = MENU.flatMap((g) => g.entrees.flatMap((e) => [e, ...(e.sous ?? [])]));
  for (const e of toutes) {
    if (e.to !== chemin) continue;
    if (!e.droit && !e.gerantsSeulement) return null;
    return { droit: e.droit, gerantsSeulement: e.gerantsSeulement };
  }
  return null;
}
